import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { ChevronLeft, Download, UploadCloud, CheckCircle2, XCircle, SkipForward, RefreshCw, FileDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "vd-admin-token";

/**
 * Bulk import (and export) auto parts via CSV. Mirrors AdminProductImport.jsx
 * (tyres) so the ops team gets a consistent workflow across both catalogues.
 */
const AdminPartsImport = () => {
  const [mode, setMode] = useState("upsert");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const onPick = (f) => {
    setErr(""); setReport(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) { setErr("Please choose a .csv file."); return; }
    setFile(f);
  };

  const downloadFromApi = async (path, filename) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${BACKEND}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message || "Download failed");
    }
  };

  const onDownloadTemplate = () => downloadFromApi("/api/admin/parts/import/template", "valuedrive-parts-template.csv");
  const onExport = () => downloadFromApi("/api/admin/parts/export/csv", "valuedrive-parts-export.csv");

  const onUpload = async () => {
    if (!file) return;
    setUploading(true); setErr(""); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await adminApi.post("/parts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-parts-import-page">
      <Link to="/admin/parts" className="inline-flex items-center gap-1 vd-muted text-sm mb-3" data-testid="parts-import-back-link">
        <ChevronLeft size={14} /> Back to Auto Parts
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div>
          <h1 style={{ fontSize: 28 }}>Bulk import auto parts</h1>
          <p className="vd-muted">Upload a CSV to create or update many parts at once.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onExport} className="vd-btn-secondary" data-testid="parts-import-export"><FileDown size={16} /> Export catalogue</button>
          <button onClick={onDownloadTemplate} className="vd-btn-secondary" data-testid="parts-import-template"><Download size={16} /> Download template</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 mt-6">
        <div className="vd-card p-6" data-testid="parts-import-uploader">
          <p className="vd-heading font-semibold mb-3">1. Choose mode</p>
          <div className="flex flex-col gap-2 mb-5">
            <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer" style={{ borderColor: mode === "upsert" ? "var(--primary-red)" : "var(--border-color)" }}>
              <input type="radio" className="vd-checkbox mt-0.5" checked={mode === "upsert"} onChange={() => setMode("upsert")} data-testid="parts-mode-upsert" />
              <div>
                <p className="vd-heading font-semibold text-sm">Upsert (recommended)</p>
                <p className="vd-muted text-xs">Rows matching an existing part by <strong>name + part_number</strong> are updated; new rows are created.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer" style={{ borderColor: mode === "create_only" ? "var(--primary-red)" : "var(--border-color)" }}>
              <input type="radio" className="vd-checkbox mt-0.5" checked={mode === "create_only"} onChange={() => setMode("create_only")} data-testid="parts-mode-create" />
              <div>
                <p className="vd-heading font-semibold text-sm">Create only</p>
                <p className="vd-muted text-xs">Existing rows are skipped — useful when seeding a fresh catalogue without overwriting live data.</p>
              </div>
            </label>
          </div>

          <p className="vd-heading font-semibold mb-3">2. Pick a CSV file</p>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-md flex flex-col items-center justify-center vd-muted text-sm py-10"
            style={{ border: "1px dashed var(--border-color)", background: "var(--bg-secondary)" }}
            data-testid="parts-import-file-button"
          >
            <UploadCloud size={28} color="var(--primary-red)" />
            <p className="vd-heading font-medium mt-2">{file ? file.name : "Click to choose a .csv"}</p>
            <p className="vd-muted text-xs mt-1">UTF-8 encoded · use “|” to separate multiple image URLs and “;” to separate fitment rows</p>
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => onPick(e.target.files?.[0])} data-testid="parts-import-file-input" />

          {err && <p className="vd-card p-3 mt-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="parts-import-error">{err}</p>}

          <button
            onClick={onUpload}
            disabled={!file || uploading}
            className="vd-btn-primary w-full justify-center mt-5"
            data-testid="parts-import-upload-btn"
          >
            {uploading ? "Importing…" : (<><RefreshCw size={16} /> Import {file ? `“${file.name}”` : "CSV"}</>)}
          </button>
        </div>

        <div className="vd-card p-6 h-fit" data-testid="parts-import-help-card">
          <p className="vd-heading font-semibold mb-3">CSV format</p>
          <p className="vd-muted text-sm mb-3">
            Headers (case-sensitive) — required marked with <span style={{ color: "var(--primary-red)" }}>*</span>:
          </p>
          <ul className="text-xs space-y-1 vd-body font-mono" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", padding: 12, borderRadius: 8 }}>
            <li>name<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>category <span className="vd-muted">(engine | suspension | brakes | body | electrical | lighting | wheels-tyres)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>part_number <span className="vd-muted">(optional)</span></li>
            <li>brand <span className="vd-muted">(optional)</span></li>
            <li>condition <span className="vd-muted">(New | Used)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>price<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>stock<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>fitment <span className="vd-muted">(Make|Model|YearFrom|YearTo, multiple rows joined by “;”, leave blank for universal)</span></li>
            <li>description <span className="vd-muted">(optional)</span></li>
            <li>image <span className="vd-muted">(single URL — optional)</span></li>
            <li>gallery <span className="vd-muted">(pipe-separated URLs — optional)</span></li>
            <li>warranty <span className="vd-muted">(optional)</span></li>
            <li>is_visible <span className="vd-muted">(true/false, default true)</span></li>
          </ul>
          <p className="vd-muted text-xs mt-3">
            Fitment example: <code className="vd-heading">Ford|Focus|2016|2020;Ford|Fiesta||</code>
            <br />→ fits Ford Focus 2016–2020 and any Ford Fiesta.
          </p>
        </div>
      </div>

      {report && (
        <div className="vd-card p-6 mt-6" data-testid="parts-import-report">
          <p className="vd-heading font-semibold mb-3">Import results</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Created" value={report.created} color="#00A35C" Icon={CheckCircle2} testid="parts-report-created" />
            <Stat label="Updated" value={report.updated} color="#3D7AFF" Icon={RefreshCw} testid="parts-report-updated" />
            <Stat label="Skipped" value={report.skipped} color="#B57E00" Icon={SkipForward} testid="parts-report-skipped" />
            <Stat label="Failed"  value={report.failed}  color="var(--primary-red)" Icon={XCircle} testid="parts-report-failed" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left vd-muted uppercase text-xs tracking-wider border-b" style={{ borderColor: "var(--border-color)" }}>
                  <th className="px-2 py-2">Row</th><th>Name</th><th>Part #</th><th>Status</th><th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {report.results.map((r) => (
                  <tr key={r.row} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`parts-report-row-${r.row}`}>
                    <td className="px-2 py-2 vd-muted">{r.row}</td>
                    <td className="vd-heading">{r.name || "—"}</td>
                    <td className="vd-muted">{r.part_number || "—"}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="vd-muted text-xs">{r.error || r.reason || (r.id ? `id ${r.id.slice(0, 8)}…` : "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-5">
            <Link to="/admin/parts" className="vd-btn-primary" data-testid="parts-report-view">View parts</Link>
            <button onClick={() => { setFile(null); setReport(null); }} className="vd-btn-secondary" data-testid="parts-report-another">Import another file</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, Icon, testid }) => (
  <div className="vd-card p-4 flex items-center gap-3" data-testid={testid}>
    <span className="inline-flex items-center justify-center rounded-md" style={{ width: 36, height: 36, background: `${color}22`, color }}>
      <Icon size={18} />
    </span>
    <div>
      <p className="vd-muted text-xs uppercase tracking-wider">{label}</p>
      <p className="vd-heading font-extrabold" style={{ fontSize: 22 }}>{value}</p>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    created: { bg: "rgba(0,180,80,0.15)", fg: "#00A35C" },
    updated: { bg: "rgba(61,122,255,0.15)", fg: "#3D7AFF" },
    skipped: { bg: "rgba(255,179,11,0.18)", fg: "#B57E00" },
    error:   { bg: "rgba(230,0,0,0.15)", fg: "var(--primary-red)" },
  };
  const c = map[status] || { bg: "var(--bg-secondary)", fg: "var(--text-heading)" };
  return <span className="text-xs font-semibold rounded px-2 py-0.5" style={{ background: c.bg, color: c.fg }}>{status}</span>;
};

export default AdminPartsImport;
